import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, LinearProgress, IconButton, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiClient } from '../../services/api';
import { computeWake } from '../../services/computeWake';

const POLL_INTERVAL_MS = 3000;
const READY_AUTO_CLOSE_MS = 2000;

// Shown when a /api/core/* request fails because the backend EC2 instance is
// stopped (idle auto-stop). Polls /api/system/compute-status, which starts the
// instance on the first call, until it reports healthy - at which point
// computeWake.resolveWake() lets every request that failed while it was down
// replay itself, so the caller picks up right where it left off instead of
// the whole page reloading. Closing the popup instead cancels the wake (see
// computeWake.cancelWake), and those requests fall back to their original error.
const EC2WakeOverlay = () => {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/system/compute-status');
      if (data?.state === 'running' && data?.healthy) {
        setReady(true);
        stopPolling();
        computeWake.resolveWake();
      }
    } catch {
      // Keep polling - transient errors are expected while the instance boots.
    }
  }, [stopPolling]);

  useEffect(() => {
    const unsubscribe = computeWake.subscribe(() => {
      setOpen((wasOpen) => {
        if (!wasOpen) {
          setReady(false);
          poll();
          stopPolling();
          pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
        }
        return true;
      });
    });
    return () => {
      unsubscribe();
      stopPolling();
    };
  }, [poll, stopPolling]);

  useEffect(() => {
    if (!ready) return undefined;
    const timer = setTimeout(() => setOpen(false), READY_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [ready]);

  const handleClose = () => {
    setOpen(false);
    stopPolling();
    if (!ready) computeWake.cancelWake();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ position: 'relative', textAlign: 'center', py: 4 }}>
        <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        {ready ? (
          <Fade in={ready}>
            <Box>
              <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body1">Up and running</Typography>
            </Box>
          </Fade>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Waking things up...
            </Typography>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This only takes a few seconds - things will be much faster right after. We pause inactive resources during our pilot to keep things efficient.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Avoid repeating the same action on another tab or device in the meantime - it'll go through here automatically once we're back.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EC2WakeOverlay;
